// A strategy is data, and this is the machine that runs it.
//
// Everything a strategy says — which sports, which prices, how many legs, how
// much to stake — is JSON with no code in it, so it can be stored, shared,
// diffed and replayed. `runStrategy` turns one of those documents plus a day's
// universe into the same { multi, legs } tickets the Today page has always
// printed, so nothing downstream has to know a strategy was involved.
//
// The tier search is the exception and stays the exception: it delegates,
// whole, to lib/tickets.js. Classic is the built-in that uses it, and a tuned
// copy of Classic is any other document with `legRule.type: 'tier'` — both go
// the same way. See strategies/classic.js.

import { TIER_ORDER, FAVOURITE, MAX_LONG_SHOTS } from '../tickets.js';
// WHAT THE BOOK WILL TAKE, beside what Kelly asks for. `limits.js` holds the
// minimum-bet schedules and the punter's own per-book caps; `trust.js` says
// which schedule a race falls under. Both are leaves.
import { bookCap } from '../limits.js';
import { codeFor, venueOfKey } from '../racing/trust.js';
import { tierFor } from '../tier.js';
import { legLink } from '../bookmakers.js';
import { fnv1a } from './hash.js';
import { CERTAINTY_METHODS, DEVIG_METHODS, fairBand } from './devig.js';
import { edge as edgeOf, ev as evOf, isActive } from './edge.js';
import { assertOneBook, combineLegs, shrinkFactor, shrinkToward } from './combine.js';
import { whelanLegs } from './whelan.js';
import {
  MIN_STAKE, budgetLeft, createBudget, expectedLogGrowth, stakeFor,
} from './kelly.js';
// The fit that lets a model speak at all, and the arithmetic of what it says
// when it does (engine review #12). See `correctedProbability`.
import { correctedProbability, correctsMarket, fitKey, fitProven } from './encompassing.js';
import { passesRules, validateRule } from './rules.js';
import { NOT_A_BET_BOOK, bestPrice, bestPriceToTake, priceAt, consensus, bookFair } from './universe.js';
// Not the model — one function off it. A single book read as the market is
// read the same way here as sharp-anchor reads it, commission and all.
import { bookFairNet } from './models/sharpAnchor.js';
import { SPORTS } from './teams.js';
import { CLASSIC, runClassic } from './strategies/classic.js';
// The second printer, for the same reason as the first: an all-up is three
// readings of one card and the ticket loop below can only ever read it once.
import {
  RACING_ALL_UP, RACING_LEAD_MS, isRacingStrategy, runRacingAllUp,
} from './strategies/racing.js';

export const STRATEGY_VERSION = 1;

/**
 * What a model strategy says on a day it has not earned the right to speak
 * (engine review #12). In the punter's voice, like the racing card's own note,
 * because it is not a fact about today's board: it is a fact about the model,
 * and it will read the same tomorrow.
 */
export const MODEL_UNPROVEN = 'This model has not yet shown it adds to the market’s price.';

/**
 * What the pad says when the day's exposure budget has nothing left in it
 * (engine review #13). The budget is the day's, shared by every strategy in
 * the deal, so this is about the DAY and not about this section.
 */
export const BUDGET_SPENT = 'Today’s exposure budget is spent.';

/** Probability sources a strategy may name. Only the first two run in this slice. */
export const KNOWN_MODELS = [
  'none',
  'market-consensus',
  'sharp-anchor',
  'elo',
  'dixon-coles',
  'close-line',
  'squiggle-ensemble',
  'cricket-toss',
  'user-upload',
];

export const LEG_RULES = ['whelan', 'fixed', 'tier', 'mix', 'singles'];
export const STAKING_TYPES = ['flat', 'fractional-kelly'];
// WHERE THE TICKET IS PLACED, and there are three answers because a multi and
// a single are not shopping for the same thing (engine review #4).
//
//   single      the book chosen in Settings, every leg.
//   best-price  the longest price on the board, leg by leg. ONE LEG ONLY: a
//               multi built this way has a combined price that exists at no
//               counter in the country, because no book will fold three other
//               books' quotes into one bet slip.
//   best-book   every leg re-priced at ONE book, and the book chosen for the
//               ticket it can actually fill. A multi's version of shopping.
export const BOOKMAKER_RULES = ['best-price', 'best-book', 'single'];
// 'win' is the race market: one runner, first past the post. It is a market of
// its own and not an h2h with twelve sides, because what a leg MEANS differs —
// an h2h leg is "this team beats that one", a win leg is "this one beats every
// other one" — and a strategy has to be able to say which it wants.
export const MARKETS = ['h2h', 'win'];

const cents = (n) => Math.round(n * 100) / 100;
const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, lo, hi) => Number.isInteger(v) && v >= lo && v <= hi;
const inUnitInterval = (v) => Number.isFinite(v) && v > 0 && v <= 1;

// --- validation --------------------------------------------------------------

const TOP_KEYS = [
  'id', 'name', 'version', 'builtin', 'description', 'filters', 'probabilitySource',
  'activeLeg', 'legRule', 'params', 'combiner', 'staking', 'bookmakerRule', 'slots',
  'minBooks',
];
const NESTED_KEYS = {
  filters: ['sports', 'markets', 'priceBand', 'daysToKickoff', 'rules'],
  probabilitySource: ['model', 'params'],
  activeLeg: ['minEdge', 'minEv', 'devig'],
  legRule: ['type', 'legs', 'max', 'tiers', 'longShots', 'shortBand', 'longBand'],
  // The tier search's knobs, and the only place a strategy carries a setting
  // the candidate engine never sees. Optional everywhere.
  params: ['longShots', 'longBand'],
  combiner: ['minLegs', 'maxLegs', 'tickets', 'distinctEvents', 'excludeSGM'],
  staking: ['type', 'fraction', 'capPct', 'exposurePct'],
};

/** The shortest price that is NOT a favourite — about $1.67. */
const LONGEST_FAVOURITE = 1 / FAVOURITE;

// A strategy is a document, not a program. Anything that smells like code in it
// is a bug or an attack, and either way it does not belong in stored JSON.
const CODEY = /=>|\bfunction\s*\(|\bnew\s+Function\b|\beval\s*\(|[;{}]/;

function scanForCode(value, path, errors) {
  if (typeof value === 'function') {
    errors.push(`${path}: a strategy may not contain functions`);
    return;
  }
  if (typeof value === 'string') {
    if (CODEY.test(value)) errors.push(`${path}: value looks like code`);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((v, i) => scanForCode(v, `${path}[${i}]`, errors));
    return;
  }
  if (isPlainObject(value)) {
    for (const [key, v] of Object.entries(value)) scanForCode(v, `${path}.${key}`, errors);
  }
}

function checkKeys(obj, allowed, path, errors) {
  for (const key of Object.keys(obj)) {
    if (!allowed.includes(key)) errors.push(`${path}: unknown key "${key}"`);
  }
}

function checkRange(value, path, errors, { min = -Infinity, ints = false }) {
  if (!Array.isArray(value) || value.length !== 2) {
    errors.push(`${path}: must be [lo, hi]`);
    return;
  }
  const [lo, hi] = value;
  const ok = (n) => (ints ? Number.isInteger(n) : Number.isFinite(n)) && n >= min;
  if (!ok(lo) || !ok(hi)) errors.push(`${path}: lo and hi must be ${ints ? 'integers' : 'numbers'} >= ${min}`);
  else if (lo > hi) errors.push(`${path}: lo must not exceed hi`);
}

/**
 * A band of decimal prices a leg has to sit inside, checked harder than
 * `checkRange`: below 1.01 is not a price a bookmaker prints, and a band with
 * no width would take nothing. True when the band is usable, so the caller can
 * skip a comparison between two bands one of which is nonsense.
 */
function checkPriceBand(value, path, errors) {
  if (!Array.isArray(value) || value.length !== 2) {
    errors.push(`${path}: must be [lo, hi]`);
    return false;
  }
  const [lo, hi] = value;
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo < 1.01) {
    errors.push(`${path}: lo and hi must be decimal prices >= 1.01`);
    return false;
  }
  if (lo >= hi) {
    errors.push(`${path}: lo must be below hi`);
    return false;
  }
  return true;
}

/** A strategy document is well-formed and every value is one we know how to run. */
export function validateStrategy(s) {
  const errors = [];
  if (!isPlainObject(s)) return { ok: false, errors: ['strategy must be an object'] };
  checkKeys(s, TOP_KEYS, 'strategy', errors);
  scanForCode(s, 'strategy', errors);

  if (typeof s.id !== 'string' || !s.id) errors.push('id: must be a non-empty string');
  if (typeof s.name !== 'string' || !s.name) errors.push('name: must be a non-empty string');
  if (!Number.isInteger(s.version) || s.version < 1) errors.push('version: must be an integer >= 1');
  if ('builtin' in s && typeof s.builtin !== 'boolean') errors.push('builtin: must be a boolean');
  if ('description' in s && typeof s.description !== 'string') errors.push('description: must be a string');

  const f = s.filters;
  if (!isPlainObject(f)) errors.push('filters: must be an object');
  else {
    checkKeys(f, NESTED_KEYS.filters, 'filters', errors);
    const sports = Object.keys(SPORTS);
    if (!Array.isArray(f.sports)) errors.push('filters.sports: must be an array');
    else if (f.sports.some((x) => !sports.includes(x))) errors.push('filters.sports: unknown sport');
    if (!Array.isArray(f.markets) || f.markets.length === 0) errors.push('filters.markets: must be a non-empty array');
    else if (f.markets.some((m) => !MARKETS.includes(m))) errors.push(`filters.markets: only ${MARKETS.join(' and ')} in v1`);
    checkRange(f.priceBand, 'filters.priceBand', errors, { min: 1 });
    checkRange(f.daysToKickoff, 'filters.daysToKickoff', errors, { min: 0, ints: true });
    if (!Array.isArray(f.rules)) errors.push('filters.rules: must be an array');
    else {
      f.rules.forEach((rule, i) => {
        const check = validateRule(rule);
        if (!check.ok) errors.push(`filters.rules[${i}]: ${check.error}`);
      });
    }
  }

  const ps = s.probabilitySource;
  if (!isPlainObject(ps)) errors.push('probabilitySource: must be an object');
  else {
    checkKeys(ps, NESTED_KEYS.probabilitySource, 'probabilitySource', errors);
    if (!KNOWN_MODELS.includes(ps.model)) errors.push(`probabilitySource.model: unknown model "${ps.model}"`);
    if (!isPlainObject(ps.params)) errors.push('probabilitySource.params: must be an object');
    else if (ps.params.devig !== undefined && !DEVIG_METHODS.includes(ps.params.devig)) {
      errors.push('probabilitySource.params.devig: unknown devig method');
    }
  }

  const a = s.activeLeg;
  if (!isPlainObject(a)) errors.push('activeLeg: must be an object');
  else {
    checkKeys(a, NESTED_KEYS.activeLeg, 'activeLeg', errors);
    if (!Number.isFinite(a.minEdge) || a.minEdge < 0) errors.push('activeLeg.minEdge: must be a number >= 0');
    if (!Number.isFinite(a.minEv) || a.minEv < 0) errors.push('activeLeg.minEv: must be a number >= 0');
    if (!DEVIG_METHODS.includes(a.devig)) errors.push('activeLeg.devig: unknown devig method');
  }

  const lr = s.legRule;
  if (!isPlainObject(lr)) errors.push('legRule: must be an object');
  else {
    checkKeys(lr, NESTED_KEYS.legRule, 'legRule', errors);
    if (!LEG_RULES.includes(lr.type)) errors.push(`legRule.type: unknown type "${lr.type}"`);
    if (lr.type === 'fixed' && !isInt(lr.legs, 1, 10)) errors.push('legRule.legs: fixed needs an integer 1..10');
    if (lr.type === 'mix') {
      if (!isInt(lr.legs, 1, 10)) errors.push('legRule.legs: mix needs an integer 1..10');
      // A ticket cannot be more long shots than it is legs.
      const legs = isInt(lr.legs, 1, 10) ? lr.legs : 10;
      if (!isInt(lr.longShots, 0, legs)) errors.push('legRule.longShots: mix needs an integer 0..legs');
      const short = checkPriceBand(lr.shortBand, 'legRule.shortBand', errors);
      const long = checkPriceBand(lr.longBand, 'legRule.longBand', errors);
      // The two bands ARE the shape. Overlapping ones would let the same price
      // be both the short leg and the long one.
      if (short && long && lr.shortBand[1] > lr.longBand[0]) {
        errors.push('legRule.shortBand: hi must not exceed legRule.longBand lo');
      }
    }
    // One leg per ticket, up to `max` of them. The cap is the rule — a singles
    // rule with no `max` would print a ticket for every active leg on the
    // board, which is a list, not a pad.
    if (lr.type === 'singles' && !isInt(lr.max, 1, 10)) {
      errors.push('legRule.max: singles needs an integer 1..10');
    }
    if (lr.type === 'tier') {
      // The tier search is no longer Classic's alone. It is still not code in
      // this module — `runStrategy` sends any tier strategy to `runClassic`,
      // which is the wrapper around the legacy engine — but a tuned copy of
      // Classic has to keep the tiers, or "tune Classic" means "stop using
      // Classic", which is what it used to mean.
      if (!Array.isArray(lr.tiers) || lr.tiers.length === 0) errors.push('legRule.tiers: must be a non-empty array');
      else if (lr.tiers.some((t) => !TIER_ORDER.includes(t))) errors.push('legRule.tiers: unknown tier');
    }
  }

  // The tier search's knobs. Optional — a document without them is the pad as
  // it shipped — and checked hard when present, because a long shot that is
  // also a favourite would be picked twice by `pickLegs`.
  if (s.params !== undefined) {
    if (!isPlainObject(s.params)) errors.push('params: must be an object');
    else {
      checkKeys(s.params, NESTED_KEYS.params, 'params', errors);
      if (s.params.longShots !== undefined && !isInt(s.params.longShots, 0, MAX_LONG_SHOTS)) {
        errors.push(`params.longShots: must be an integer 0..${MAX_LONG_SHOTS}`);
      }
      if (s.params.longBand !== undefined
        && checkPriceBand(s.params.longBand, 'params.longBand', errors)
        && s.params.longBand[0] <= LONGEST_FAVOURITE) {
        errors.push(`params.longBand: lo must be longer than ${LONGEST_FAVOURITE.toFixed(2)}: a favourite is not a long shot`);
      }
    }
  }

  const c = s.combiner;
  if (!isPlainObject(c)) errors.push('combiner: must be an object');
  else {
    checkKeys(c, NESTED_KEYS.combiner, 'combiner', errors);
    if (!isInt(c.minLegs, 1, 10)) errors.push('combiner.minLegs: must be an integer 1..10');
    if (!isInt(c.maxLegs, 1, 10)) errors.push('combiner.maxLegs: must be an integer 1..10');
    if (isInt(c.minLegs, 1, 10) && isInt(c.maxLegs, 1, 10) && c.minLegs > c.maxLegs) {
      errors.push('combiner.minLegs: must not exceed maxLegs');
    }
    if (!isInt(c.tickets, 1, 4)) errors.push('combiner.tickets: must be an integer 1..4');
    if (c.distinctEvents !== true) errors.push('combiner.distinctEvents: must be true');
    if (c.excludeSGM !== true) errors.push('combiner.excludeSGM: must be true');
  }

  const st = s.staking;
  if (!isPlainObject(st)) errors.push('staking: must be an object');
  else {
    checkKeys(st, NESTED_KEYS.staking, 'staking', errors);
    if (!STAKING_TYPES.includes(st.type)) errors.push(`staking.type: unknown type "${st.type}"`);
    if (st.type === 'fractional-kelly') {
      if (!inUnitInterval(st.fraction)) errors.push('staking.fraction: must be in (0, 1]');
      if (!inUnitInterval(st.capPct)) errors.push('staking.capPct: must be in (0, 1]');
      if (!inUnitInterval(st.exposurePct)) errors.push('staking.exposurePct: must be in (0, 1]');
    }
  }

  // How many books had to vote in the consensus before it counts as a market.
  // Optional: a strategy without it takes whatever the board offered, which is
  // how every strategy behaved before this existed. One book agreeing with
  // itself is not a market, so the floor starts at 2.
  if (s.minBooks !== undefined && !isInt(s.minBooks, 2, 20)) {
    errors.push('minBooks: must be an integer 2..20');
  }

  if (!BOOKMAKER_RULES.includes(s.bookmakerRule)) errors.push(`bookmakerRule: unknown rule "${s.bookmakerRule}"`);
  // A BEST-PRICE MULTI IS NOT A BET (engine review #4). Best price shops leg by
  // leg, so a ticket of more than one leg holds prices from more than one book
  // and the combined odds printed on it are not offered anywhere. The rule is
  // refused at validation rather than quietly rewritten, because a strategy
  // document is a promise about what will be printed: `best-book` is the multi's
  // answer and it is a different search, not a spelling of this one.
  else if (s.bookmakerRule === 'best-price' && s.legRule?.type && s.legRule.type !== 'singles') {
    errors.push('bookmakerRule: best-price is for singles only; a multi shops with best-book');
  }
  if (!isInt(s.slots, 1, 4)) errors.push('slots: must be an integer 1..4');

  return errors.length ? { ok: false, errors } : { ok: true };
}

// --- running -----------------------------------------------------------------

// WHICH RISK A TICKET PRINTS is `lib/tier.js`'s, and only its: the bands, the
// function, and the rule that nothing else in the app may write a tier string.
// Re-exported here because this is where every caller has always reached for
// it, and because a strategy printing a ticket is the one place it is used.
export { tierFor };

/**
 * WHAT A LEG IS CALLED, in one place for both kinds of market.
 *
 * A ticket says two things about every leg: what was backed and where it is
 * running. For a fixture that is "Collingwood" and "Collingwood v Carlton"; for
 * a race it is "Winx" and "Randwick R5". They were two lines of string-building
 * inside the candidate loop, and a race has no `home` to build them from.
 *
 * `number` is the saddlecloth, and it comes back here rather than being looked
 * up again later because the LEG is printed from the candidate, not from the
 * match — by then the runners are out of reach. It is null for a fixture, which
 * is what keeps a football leg's printed text byte-for-byte what it always was.
 */
export function describe(match, selection) {
  const runner = match?.runners?.[selection];
  if (runner) {
    return {
      team: runner.name ?? String(selection),
      match: match.raceLabel ?? `${match.venue} R${match.raceNumber}`,
      number: runner.number ?? null,
    };
  }
  return {
    team: match?.[selection]?.name ?? (selection === 'draw' ? 'Draw' : String(selection)),
    match: `${match?.home?.name} v ${match?.away?.name}`,
    number: null,
  };
}

/** A leg's printed side: the runner's number rides with the name where there is one. */
const legSelection = (c) => `${c.number ? `${c.team} (${c.number})` : c.team} to win`;

function liquidityAt(match, selection, bookmaker) {
  const row = match.prices?.[selection]?.find((r) => r.bookmaker === bookmaker);
  return row?.liquidity ?? null;
}

/**
 * THE FIVE REASONS A LEG IS TURNED DOWN, and they are five different findings.
 *
 *   unproven   the model has not earned a price in this sport yet.
 *   ev         the price does not pay enough over what the leg is worth.
 *   edge       the claim is not far enough from the market's own number.
 *   certainty  it cleared the strategy's own devig and not all three of them,
 *              so the edge is a choice of arithmetic rather than a finding.
 *   budget     the day's exposure was already spent when it came up.
 *
 * A BOOK'S OWN CAP IS NOT ONE OF THEM, deliberately. `stakeAllowed` refuses a
 * STAKE the punter is trying to place on a ticket the app did propose; the
 * ticket is on the trail already and counting it here would be one leg recorded
 * twice, once as proposed and once as refused.
 */
export const REFUSAL_REASONS = ['unproven', 'ev', 'edge', 'certainty', 'budget'];

/**
 * One refused leg, in the smallest shape the paper trail can settle: enough to
 * name it, enough to price it, and enough for `resolveLeg` to find its result
 * and its close tomorrow morning.
 */
function refusal(match, selection, bookmaker, price, reason) {
  const named = describe(match, selection);
  return {
    key: `${match.matchKey}:${selection}`,
    matchKey: match.matchKey,
    providerEventId: match.providerEventId ?? null,
    sport: match.sport,
    sportTitle: match.sportTitle ?? match.competition,
    kickoff: match.kickoff,
    side: selection,
    selection: `${named.number ? `${named.team} (${named.number})` : named.team} to win`,
    match: named.match,
    bookmaker,
    bookmakerName: bookmakerNameAt(match, selection, bookmaker),
    price,
    reason,
  };
}

/**
 * The same row off a CANDIDATE rather than off the board. The budget refusal
 * happens long after the board has been walked — the leg has already been
 * named, priced and screened — and rebuilding it from a match that is no longer
 * in hand would be looking the same thing up twice.
 */
function refusalOf(c, reason) {
  return {
    key: c.key,
    matchKey: c.matchKey,
    providerEventId: c.providerEventId ?? null,
    sport: c.sport,
    sportTitle: c.sportTitle ?? c.competition,
    kickoff: c.kickoff,
    side: c.selection,
    selection: legSelection(c),
    match: c.match,
    bookmaker: c.bookmaker,
    bookmakerName: c.bookmakerName,
    price: c.price,
    reason,
  };
}

/**
 * How far a price is over the Betfair Hub's rated price for that runner, or
 * null where nobody rated it.
 *
 * The rating rides on the runner (`match.runners[rN].rating`), put there by the
 * daily features job on a live card and by the `--ratings` ingest on a stored
 * one. It is a SECOND OPINION and it never moves a price: this is the one
 * number it produces, and a strategy has to name it in a rule before it means
 * anything at all.
 */
/**
 * The cap the book this ticket is priced at will take, or null.
 *
 * `ctx.bookLimits` is the punter's own record (`profile.bookLimits`); where it
 * is absent the racing minimum-bet schedule stands in, and a sport bet has no
 * cap at all. A ticket whose legs are at more than one book is capped by
 * nothing: there is no one shop to ask.
 */
function bookCapFor(ctx, legs) {
  const keys = new Set(legs.map((c) => c.bookmaker).filter(Boolean));
  if (keys.size !== 1) return null;
  const [key] = [...keys];
  const sport = legs[0]?.sport ?? '';
  const code = String(sport).startsWith('racing_')
    ? codeFor(sport, venueOfKey(legs[0]?.matchKey))
    : 'sport';
  return bookCap(ctx.bookLimits, key, { code });
}

function ratingGap(match, selection, price) {
  const rated = Number(match?.runners?.[selection]?.rating?.ratedPrice);
  if (!Number.isFinite(rated) || rated <= 1 || !Number.isFinite(price)) return null;
  return price / rated - 1;
}

function bookmakerNameAt(match, selection, bookmaker) {
  const row = match.prices?.[selection]?.find((r) => r.bookmaker === bookmaker);
  return row?.bookmakerName ?? bookmaker;
}

/** A decimal price inside a [lo, hi] band, both ends included. */
const inBand = (price, [lo, hi]) => price >= lo && price <= hi;

/**
 * THE CERTAINTY GATE'S FLOORS — the strategy's own, a hair's breadth lower.
 *
 * The gate asks the same leg three times, once per devig, and a leg only prints
 * if it clears the floors every time (engine review #3). Three readings that
 * differ in the ninth decimal are not three opinions. Power and Shin are both
 * BISECTIONS — `powerK` and `shinZ` stop when the vector sums to within 1e-9 of
 * one — so a market that is exactly even comes back as 0.5 from multiplicative
 * and 0.49999999953 from Shin. Against a zero floor the second of those is a
 * rejection, and the leg has been dropped for the solver's stopping rule rather
 * than for a disagreement about the price.
 *
 * So the floors are relaxed by rather more than that tolerance and rather less
 * than anything a punter could see: a millionth of a probability point is four
 * orders below the smallest edge the app will ever print.
 */
const GATE_EPS = 1e-6;

/**
 * Two books' tickets are worth the same when they are worth the same to a
 * twelfth decimal place: floating-point multiplication of four prices does not
 * give the same bits in two different orders, and a tie broken by the last bit
 * would deal one ticket on one device and another on the next.
 */
const TIE_EPS = 1e-12;
const gateFloorsFor = (activeLeg) => ({
  minEdge: activeLeg.minEdge - GATE_EPS,
  minEv: activeLeg.minEv - GATE_EPS,
});

/**
 * Legs by SHAPE: `longShots` of them out of the long band, the rest out of the
 * short one, the likeliest first in each. This is the one leg rule that asks
 * nothing about value — Classic's tiers move leg count and leg length
 * together, and a mix is what separates the two dials: "six legs, all short
 * favourites" and "three short legs and two long shots" are both sayable here
 * and neither is sayable as a tier.
 *
 * `ranked` arrives one leg per match — `runStrategy` has already taken the
 * distinct-event rule off the top — and `used` is what stops a leg that sits
 * in both bands (they are allowed to touch at a price) being counted twice.
 *
 * A long band that came up short is not a reason to print a leg down: the fill
 * pass takes the total to `legs` out of the short band whatever the first pass
 * found, and the caller notes what was missing.
 */
function mixLegs(rule, ranked, combiner) {
  const want = Math.min(rule.legs, combiner.maxLegs);
  // Likeliest first, with the list's own order — edge, then the seeded rank —
  // breaking a tie, so one board deals one ticket however often it is run.
  const likeliestFirst = (list) => list
    .map((c, i) => ({ c, i }))
    .sort((a, b) => b.c.pFair - a.c.pFair || a.i - b.i)
    .map((x) => x.c);

  const picked = [];
  const used = new Set();
  const take = (band, upTo) => {
    for (const c of likeliestFirst(ranked.filter((x) => inBand(x.price, band)))) {
      if (picked.length >= upTo) break;
      if (used.has(c.matchKey)) continue;
      used.add(c.matchKey);
      picked.push(c);
    }
  };
  take(rule.longBand, Math.min(rule.longShots, want));
  take(rule.shortBand, want);
  return picked;
}

function chooseLegs(legRule, ranked, combiner) {
  if (legRule.type === 'whelan') return whelanLegs(ranked, { max: combiner.maxLegs });
  if (legRule.type === 'fixed') return ranked.slice(0, Math.min(legRule.legs, combiner.maxLegs));
  if (legRule.type === 'mix') return mixLegs(legRule, ranked, combiner);
  // ONE LEG, and the next call gets the next one. The ticket loop already
  // hands this the pool with every match already on a ticket taken out, so
  // "the best remaining leg" is all this rule has to say; how many tickets get
  // printed is `legRule.max`, decided by the caller.
  if (legRule.type === 'singles') return ranked.slice(0, 1);
  return [];
}

/**
 * Run one strategy over one day's universe.
 *
 * ctx: { asOf, forDate, seed, stake, bankroll, bookmaker, bookmakerName,
 *        timeZone, sameDay, oddsFetchedAt, tiersShown, models, modelFits,
 *        probSets, marketBook, budget }
 *
 * `modelFits` is `${modelId}:${sport}` -> the encompassing fit that model has
 * earned (engine review #12); without one a model strategy prints nothing.
 * `budget` is the DAY's exposure, shared by every strategy in one deal (#13) —
 * a run handed none makes its own, and caps itself alone.
 *
 * `marketBook` is the punter's answer to "which book is the market?" — absent
 * or 'consensus' means the whole board, anything else names one book. Only a
 * market-consensus strategy reads it: a model strategy's edge is its model
 * against the price it is taking, and Classic measures no edges at all.
 *
 * Same inputs and same seed always give the same tickets: nothing here reads
 * the clock, and every sort has an explicit tie-break.
 */
export function runStrategy(strategy, universe, ctx = {}) {
  // Classic, or anything that picks by tier — a tuned copy of Classic is still
  // the tier search, and routing on the leg rule rather than the id is what
  // keeps it one. Nothing below this line knows what a tier is.
  if (strategy?.id === CLASSIC.id || strategy?.legRule?.type === 'tier') {
    return runClassic(universe, ctx, strategy);
  }
  // The all-up, routed on its id rather than on a shape, because there is no
  // shape to route on: "three tickets of the same card at two, three and four
  // legs" is not something a leg rule can say. A fork of it runs through the
  // ordinary engine, which is the honest answer — a fork has changed the very
  // thing that made it need its own printer.
  if (strategy?.id === RACING_ALL_UP.id) {
    return runRacingAllUp(universe, ctx, strategy);
  }

  const check = validateStrategy(strategy);
  if (!check.ok) {
    return {
      tickets: [], candidates: [], refused: [], notes: check.errors.map((e) => `invalid strategy: ${e}`),
    };
  }

  const notes = [];
  const { filters, activeLeg, legRule, combiner, staking, probabilitySource, bookmakerRule } = strategy;
  const gateFloors = gateFloorsFor(activeLeg);
  const seed = ctx.seed ?? 0;
  const forDate = ctx.forDate ?? universe.forDate;
  const asOfMs = new Date(ctx.asOf ?? universe.asOf).getTime();
  const [priceLo, priceHi] = filters.priceBand;
  const [dayLo, dayHi] = filters.daysToKickoff;
  const modelId = probabilitySource.model;

  // A strategy with no model has no opinion, so there is nothing for the edge
  // and EV thresholds to be a threshold ON, and they are not asked of it.
  // They also cannot be met: with `model: 'none'` pModel IS the book's own
  // fair price, so every leg's edge is exactly 0 and its EV is exactly minus
  // that book's margin — negative on every board a bookmaker would print. A
  // screen that can only ever reject the lot is an off switch, not a screen.
  // What a no-model strategy chooses by is shape (legRule "mix") or price
  // band; every other source still clears both thresholds, leg by leg.
  const screens = modelId !== 'none';

  // ONE LEG A TICKET. Flagged once here because it moves three things at the
  // far end of the run — which books may be bet at, how many tickets print,
  // and whose name goes on them — and reading `legRule.type` in three places
  // is three chances for them to disagree.
  const singles = legRule.type === 'singles';

  // A CARD, NOT A FIXTURE LIST, and a race jumping in four minutes is not a
  // proposal. `daysToKickoff` is the only time filter the engine has and its
  // finest grain is a whole day, which on a card is no filter at all: every
  // race on it is day 0, including the one in the gates. Read off the document
  // rather than off a list of ids, so a punter's fork of Racing value gets the
  // same lead time the strategy it was forked from has. See `RACING_LEAD_MS`.
  const racing = isRacingStrategy(strategy);

  // How many books have to have voted in the consensus before it is believed.
  // Optional, and only ever asked of the consensus itself: a named market book
  // is one book by the punter's own choice, and a model is not a vote count.
  const minBooks = Number.isFinite(strategy.minBooks) ? strategy.minBooks : 0;

  // One book as the market, or null for the board. Counted as it goes, because
  // a market book that priced half the day is a different thing from one that
  // priced none of it, and the ticket cannot say which.
  const marketBook = ctx.marketBook && ctx.marketBook !== 'consensus' ? ctx.marketBook : null;

  let predict = null;
  if (modelId !== 'none' && modelId !== 'market-consensus') {
    const model = ctx.models?.[modelId];
    if (typeof model?.predict !== 'function') {
      notes.push(`no model supplied for "${modelId}"`);
      return {
        tickets: [], candidates: [], refused: [], notes,
      };
    }
    predict = model.predict.bind(model);
  }

  // THE FIT THAT LETS THIS MODEL SPEAK, one per sport, because a model that
  // adds to the AFL market has said nothing about the NRL one. `ctx.modelFits`
  // is keyed `${modelId}:${sport}` — the same key the model store files a state
  // under — and a model that IS a market price (`none`, `market-consensus`,
  // `sharp-anchor`) is never asked for one.
  const corrects = correctsMarket(modelId);
  const fitOf = (match) => (corrects ? ctx.modelFits?.[fitKey(modelId, match?.sport)] ?? null : null);

  // --- candidates ------------------------------------------------------------
  //
  // WHERE A LEG IS PRICED, as a function rather than a branch in the loop, so
  // that the same pass can be run once for the profile's book and once per book
  // on the board for `best-book` (engine review #4). Each answers
  // `{ bookmaker, price, viaBestPrice }` or null for "not available here".
  const atBestPrice = (match, selection) => {
    const best = singles ? bestPriceToTake(match, selection) : bestPrice(match, selection);
    return best ? { bookmaker: best.bookmaker, price: best.price, viaBestPrice: false } : null;
  };
  const atProfileBook = (match, selection) => {
    const price = priceAt(match, selection, ctx.bookmaker);
    if (price !== null) return { bookmaker: ctx.bookmaker, price, viaBestPrice: false };
    // A RACE THE PUNTER'S OWN BOOK IS NOT PRICING falls back to the best price
    // they could walk in and take, marked, exactly as the leg picker does
    // (`bestPriceUsed` in engine/reprice.js). Fourteen books quote an Australian
    // race and no two of them quote the same fourteen, so a strategy that
    // dropped every race its shop had missed would print a card with holes in
    // it and never say where they were.
    //
    // Races only, and `match.runners` is the one field that makes a match a
    // race. A fixture the profile's book is not pricing stays dropped: the
    // sports board carries one market per fixture and a book absent from it has
    // nothing to say, which is not the same thing at all.
    if (!match.runners) return null;
    const shop = bestPriceToTake(match, selection);
    return shop ? { bookmaker: shop.bookmaker, price: shop.price, viaBestPrice: true } : null;
  };
  // ONE NAMED BOOK AND NO FALLBACK. A leg that book is not pricing is a leg
  // that ticket cannot carry — which is the whole of what makes the ticket
  // placeable, and the reason a book that is best on two legs and dead on the
  // third loses the ticket to one that prices all three.
  const atBook = (key) => (match, selection) => {
    const price = priceAt(match, selection, key);
    return price === null ? null : { bookmaker: key, price, viaBestPrice: false };
  };

  const priceLeg = bookmakerRule === 'best-price' ? atBestPrice : atProfileBook;

  function collect(pickBook) {
    const candidates = [];
    // EVERY LEG THE GATE TURNED DOWN, WITH THE PRICE IT WOULD HAVE TAKEN.
    //
    // A refusal is a decision, and a decision nobody wrote down is a decision
    // nobody can check. The paper trail settles these beside the proposals
    // (`lib/rejections.js`), gives each one a closing price like any other leg,
    // and Insights prints what the bar cost: a bar that keeps refusing legs
    // that went on to beat the close is a bar set wrong, and there is no way to
    // learn that from the tickets that printed.
    //
    // Cheap by construction: one small object per refused leg, built where the
    // refusal already happens, and the caller caps the day at fifty.
    const refused = [];
    const stats = {
      thinMarket: 0, marketLegs: 0, marketAbsent: 0, marketSelf: 0, unproven: 0, noMarket: 0,
    };
    // Counted by RACE and not by runner: fourteen refusals in one field is one
    // race nobody can back, and a note that said fourteen would be arithmetic
    // about the app rather than about the card.
    const tooSoon = new Set();
    for (const match of universe.matches.values()) {
      if (filters.sports.length && !filters.sports.includes(match.sport)) continue;
      if (racing) {
        const jumpsIn = Date.parse(match.kickoff ?? '') - asOfMs;
        if (!Number.isFinite(jumpsIn) || jumpsIn < RACING_LEAD_MS) {
          // Only races still to come, for the reason `runRacingAllUp` gives:
          // a race that has been and gone was not skipped, it was spent.
          if (jumpsIn >= 0) tooSoon.add(match.matchKey);
          continue;
        }
      }
      // A race brings its own sides — its ACTIVE field, scratchings already gone.
      // Deliberately not `selectionsOf`: that one's default carries the draw, and
      // draws are not legs in v1, the same as buildPool in tickets.js.
      for (const selection of match.selections ?? ['home', 'away']) {
        if (!match.prices?.[selection]?.length) continue;

        const at = pickBook(match, selection);
        if (!at) continue;
        const { bookmaker, price, viaBestPrice } = at;
        if (price < priceLo || price > priceHi) continue;

        const hoursToKickoff = (new Date(match.kickoff).getTime() - asOfMs) / 3_600_000;
        const day = Math.floor(hoursToKickoff / 24);
        if (day < dayLo || day > dayHi) continue;

        // pFair is what the price being taken is worth with its own book's
        // margin stripped — not a board average, which would dilute a real
        // mispricing by the number of books voting. edge = pModel - pFair then
        // reads "the market thinks this is likelier than my book's price says".
        //
        // ONCE PER METHOD, not once (engine review #3). The strategy's declared
        // devig is what gets PRINTED, and it is `fairBy[activeLeg.devig]` below;
        // the other two are there so the screen can be asked whether this leg is
        // a finding or a choice of arithmetic. They answer null together or not
        // at all — an incomplete or insane vector is a fact about the board, not
        // about the method — so one check covers the lot.
        const fairMethods = [...new Set([...CERTAINTY_METHODS, activeLeg.devig])];
        const fairBy = {};
        for (const m of fairMethods) fairBy[m] = bookFair(match, selection, bookmaker, m);
        const pFair = fairBy[activeLeg.devig];
        if (pFair === null || pFair === undefined) continue;
        let books = 1;

        // The same three readings of what the leg is WORTH, where that figure is
        // itself a devig. Null for a real model: an Elo number does not move
        // because the app changed how it strips a margin, so the gate below
        // measures a model's claim against all three fair prices and the model's
        // own claim stays put.
        let modelBy = null;
        let pModel;
        // The model's own untouched claim, and the weight the fit gave it —
        // both carried onto the leg so a blended figure can be explained
        // (`blendWeight` on the printed leg) rather than just being smaller.
        let pRaw = null;
        let blendWeight = null;
        // WHERE THE MARKET HALF OF THIS LEG CAME FROM (F1b): 'sharp' when a
        // sharp reference priced the side and IS the fair price, 'local' when
        // it is the local board added up. Null on a leg whose probability is
        // the book's own price devigged — there is no market reading to source
        // — so a Classic leg and every leg stored before this reads as it did.
        let fairMode = null;
        let fairSource = null;
        if (modelId === 'none') {
          pModel = pFair;
          modelBy = fairBy;
        } else if (modelId === 'market-consensus') {
          const method = probabilitySource.params?.devig ?? activeLeg.devig;
          const methods = [...new Set([...CERTAINTY_METHODS, method])];

          // A named market book is the whole opinion, devigged on its own book
          // and net of commission if it charges any. Two things send the run
          // back to the board: a book cannot be its own market, and a book that
          // is not pricing this game has nothing to say about it.
          let named = null;
          if (marketBook) {
            stats.marketLegs += 1;
            if (marketBook === bookmaker) stats.marketSelf += 1;
            else {
              named = {};
              for (const m of methods) named[m] = bookFairNet(match, selection, marketBook, m);
              if (named[method] === null) {
                named = null;
                stats.marketAbsent += 1;
              }
            }
          }

          if (named) {
            pModel = named[method];
            modelBy = named;
            books = 1;
          } else {
            // The rest of the board is the opinion; the book being bet at does
            // not get to vote on its own fairness.
            const view = consensus(match, selection, { devig: method, exclude: bookmaker });
            if (!view) continue;
            // Two books disagreeing is not a market to call a third one wrong
            // with. Counted, not announced per leg — see the note below.
            if (minBooks && view.books < minBooks) {
              stats.thinMarket += 1;
              continue;
            }
            pModel = view.pFair;
            books = view.books;
            fairMode = view.mode ?? null;
            fairSource = view.source ?? null;
            modelBy = {};
            for (const m of methods) {
              modelBy[m] = m === method
                ? view.pFair
                : (consensus(match, selection, { devig: m, exclude: bookmaker })?.pFair ?? null);
            }
          }
        } else if (!corrects) {
          // sharp-anchor: the "model" is one sharp book's price with the margin
          // and the commission off, which is a market price already. There is
          // nothing for a market correction to correct, so this reads exactly
          // as it always has.
          const p = predict(match, selection);
          if (!Number.isFinite(p) || p <= 0 || p >= 1) continue;
          pModel = p;
        } else {
          // THE MODEL CORRECTS THE MARKET; IT DOES NOT REPLACE IT (engine
          // review #12). `pModel` was `predict()` — the raw model, standing in
          // for the price — while `encompassing.js` sat in the backtest summary
          // saying "bet the blend, never the raw model" and being read by
          // nobody. What gets bet now is the blend that regression fitted, on
          // coefficients that ride with the model's own state:
          //
          //     sigmoid(b0 + b1 · logit(pMarket) + b2 · logit(pRaw))
          //
          // and where there are no coefficients, or `b2`'s t-statistic is under
          // 1.96, there is no leg at all. See `MODEL_UNPROVEN`.
          const fit = fitOf(match);
          if (!fitProven(fit)) {
            stats.unproven += 1;
            // Written down rather than only counted: this is the refusal the
            // app makes most often, and the one it most needs to be able to
            // score itself on later.
            refused.push(refusal(match, selection, bookmaker, price, 'unproven'));
            continue;
          }
          const p = predict(match, selection);
          if (!Number.isFinite(p) || p <= 0 || p >= 1) continue;

          // The market the correction is applied to is E2's consensus with the
          // candidate's own book left out — the same market every other reading
          // on this leg is taken against, and for the same reason: the book
          // being bet at does not get a vote on whether its own price is fair.
          const method = probabilitySource.params?.devig ?? activeLeg.devig;
          const methods = [...new Set([...CERTAINTY_METHODS, method])];
          const marketBy = {};
          for (const m of methods) {
            marketBy[m] = consensus(match, selection, { devig: m, exclude: bookmaker })?.pFair ?? null;
          }
          const view = marketBy[method] === null
            ? null
            : consensus(match, selection, { devig: method, exclude: bookmaker });
          // NO MARKET, NO CORRECTION. A one-book board gives the blend nothing
          // to correct, and falling back to the raw model here would be the
          // whole of what #12 says not to do.
          if (!view) {
            stats.noMarket += 1;
            continue;
          }
          if (minBooks && view.books < minBooks) {
            stats.thinMarket += 1;
            continue;
          }
          pModel = correctedProbability(fit, view.pFair, p);
          if (!Number.isFinite(pModel)) continue;
          books = view.books;
          fairMode = view.mode ?? null;
          fairSource = view.source ?? null;
          blendWeight = fit.b2;
          pRaw = p;
          // The certainty gate reads the blend once per devig method, because
          // the MARKET half of it moves with the method even though the model's
          // own claim does not.
          modelBy = {};
          for (const m of methods) {
            modelBy[m] = marketBy[m] === null ? null : correctedProbability(fit, marketBy[m], p);
          }
        }

        // HOW SURE THE APP IS OF THAT FAIR PRICE, in one number: the width of the
        // three methods' band over its own middle. Carried onto the leg as
        // `fairSpread` and printed as a RANGE where it is wide (Ticket.vue).
        const band = fairBand(CERTAINTY_METHODS.map((m) => fairBy[m]));

        const named = describe(match, selection);
        const candidate = {
          key: `${match.matchKey}:${selection}`,
          matchKey: match.matchKey,
          providerEventId: match.providerEventId ?? null,
          sport: match.sport,
          competition: match.competition,
          // What the LEG calls its sport. The competition for a fixture, as ever;
          // the code's own name for a race, because there the competition is the
          // meeting and the race label beside it already says that.
          sportTitle: match.sportTitle ?? match.competition,
          kickoff: match.kickoff,
          // The feed's own word for "it has jumped", where there is one. Null on
          // every fixture, which is what keeps a football leg what it was.
          inplayAt: match.inplayAt ?? null,
          marketClosedAt: match.marketClosedAt ?? null,
          selection,
          team: named.team,
          match: named.match,
          number: named.number,
          bookmaker,
          viaBestPrice,
          bookmakerName: bookmakerNameAt(match, selection, bookmaker),
          // The book's own page for this bet, where the row that priced it
          // published one — `{ link, linkKind }`, or nothing at all. Read off the
          // same row `bookmakerNameAt` reads, so it can only ever be the link of
          // the book this candidate is taking the price at.
          ...legLink(match, selection, bookmaker),
          price,
          liquidity: liquidityAt(match, selection, bookmaker),
          // THE BETFAIR HUB'S OWN OPINION OF THIS RUNNER, as a gap rather than
          // as a price: `price / ratedPrice - 1`, so +0.08 is "the board is
          // eight per cent longer than the Hub thinks it is worth". Null on
          // every fixture, on every race nobody has rated, and on harness,
          // which nobody rates at all — and a rule about a field a candidate
          // has not got refuses that candidate, which is the honest reading of
          // "no second opinion" for a strategy that asked for one.
          ratingDisagreement: ratingGap(match, selection, price),
          pFair,
          // The band the three devigs put that fair price in, and how wide it is
          // relative to its middle. Null on a market nobody has a second opinion
          // about, which is what a screen prints one number for.
          pFairLow: band?.low ?? null,
          pFairHigh: band?.high ?? null,
          pUncertainty: band?.spread ?? null,
          pModel,
          p: pModel,
          // The raw model behind a blended claim, and the weight the fit put on
          // it. Null on everything else, which is every leg the app printed
          // before #12 and every leg a market strategy prints now.
          pRaw,
          blendWeight,
          fairMode,
          fairSource,
          edge: edgeOf(pModel, pFair),
          ev: evOf(pModel, price),
          // What repeatedly betting this leg would do to a bankroll — the figure
          // the pool is RANKED by. `edge` and `ev` above are unchanged and are
          // what the screens print. See `expectedLogGrowth` in kelly.js.
          //
          // ZERO WITH NO MODEL, and that is not a shortcut. `pModel` is then the
          // book's OWN price devigged, so there is no opinion to grow against:
          // the figure it would produce is a fact about the book's round, and on
          // an underround vector (an exchange, a two-way market a shop has priced
          // wide) it comes out positive and would order a pool by an artefact.
          // Every such leg tied at exactly zero on `edge` too, which is what the
          // seeded tie-break below has always been for.
          growth: screens ? expectedLogGrowth(pModel, price) : 0,
          hoursToKickoff,
          isFavourite: pFair >= FAVOURITE,
          books,
          active: false,
        };
        if (!passesRules(filters.rules, candidate)) continue;
        // THE CERTAINTY GATE (engine review #3). A leg is active only if it
        // clears the edge and EV floors under EVERY devig — read consistently,
        // the leg's fair price and the market's opinion of it stripped by the
        // same method, because mixing two methods' answers compares arithmetic
        // rather than prices.
        //
        // It bites where it should. On a favourite the three agree to a point or
        // two and the gate is invisible. On the tail of a big field they disagree
        // by tens of per cent, and a leg whose whole edge is the difference
        // between Shin's story about the margin and multiplicative's is a leg the
        // app was printing because of how it does its sums. `isActive` is still
        // the one screen; it is just asked three times.
        const readings = [...new Set([...CERTAINTY_METHODS, activeLeg.devig])]
          .map((m) => ({
            pModel: modelBy ? modelBy[m] : pModel,
            pFair: fairBy[m],
            price,
          }))
          .filter((r) => Number.isFinite(r.pModel) && Number.isFinite(r.pFair));
        candidate.active = screens
          ? readings.length > 0 && readings.every((r) => isActive(r, gateFloors))
          : true;
        // WHICH OF THE THREE FLOORS IT FELL AT, told apart rather than lumped
        // together: "below the EV floor" and "the three devigs disagreed" are
        // different findings about the same leg, and only the second one is an
        // argument about arithmetic.
        if (screens && !candidate.active) {
          const reason = candidate.ev < activeLeg.minEv - GATE_EPS
            ? 'ev'
            : candidate.edge < activeLeg.minEdge - GATE_EPS ? 'edge' : 'certainty';
          refused.push(refusal(match, selection, bookmaker, price, reason));
        }
        candidates.push(candidate);
      }
    }
    // Match insertion order follows the feed; sorting here means a reordered
    // snapshot cannot reorder the output.
    candidates.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    refused.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    stats.tooSoon = tooSoon.size;
    return { candidates, refused, stats };
  }

  const rank = (c) => fnv1a(`${forDate}|${strategy.id}|${seed}|${c.key}`);
  // GROWTH DECIDES; EQUAL GROWTH, TAKE THE LIKELIER LEG — except under a shape
  // rule.
  //
  // This sorted on `edge` — probability points — until engine review #2, and
  // points are not comparable across prices: three points at $1.55 and three
  // points at $13 ranked equal and the hash picked between them. Expected log
  // growth (`kelly.js`) is what the two differ by, and it puts the favourite
  // first, which is the answer a punter would have given without arithmetic.
  //
  // NOTHING ABOUT A NO-MODEL STRATEGY MOVES. With `model: 'none'` every leg's
  // EV is exactly minus the book's margin, so `f*` is zero, so growth is zero
  // on every candidate — the same universal tie `edge` used to produce — and
  // the run falls through to the same two tie-breaks it always did. The hash
  // was then choosing which side of a match — which RUNNER of a fourteen-horse
  // race — got into the pool at all, because only the first candidate per event
  // reaches the leg rule, which is why the probability tie-break is there.
  //
  // `mix` is the exception and it is not an edge case: "three short prices and
  // two long ones" needs both ends of the market in the pool, and a pool of
  // favourites has no long shots left in it to find. A shape rule does its own
  // choosing, so it is handed the board the way it always was.
  const shaped = legRule.type === 'mix';
  const rankedOf = (list) => list.filter((c) => c.active)
    .sort((a, b) => b.growth - a.growth || (shaped ? 0 : b.p - a.p) || rank(a) - rank(b));

  // THE POOL ONE TICKET DRAWS FROM: the ranked legs, one per event, because the
  // distinct-event rule is taken off the top before any leg rule sees them.
  const poolOf = (list) => {
    const seen = new Set();
    const pool = [];
    for (const c of list) {
      if (seen.has(c.matchKey)) continue;
      seen.add(c.matchKey);
      pool.push(c);
    }
    return pool;
  };

  // WHAT A SET OF LEGS IS WORTH AS ONE BET, with the estimation-error haircut
  // already taken (engine review #10) — the same number the ticket will print,
  // so the book chosen below is chosen on the figure a punter can read off the
  // paper rather than on a brighter one used only for the choosing.
  const ticketEv = (picked) => {
    const n = picked.length;
    const combined = combineLegs(picked.map((c) => ({
      ...c, p: shrinkToward(c.pModel, c.pFair, n),
    })));
    return combined.p * combined.price - 1;
  };

  // --- which book ------------------------------------------------------------
  //
  // ONE BOOK, CHOSEN FOR THE TICKET IT CAN FILL (engine review #4). Every book
  // on the board that a punter could walk into gets the whole run done at its
  // own prices: each leg re-priced there, re-screened there under the certainty
  // gate, and the leg rule run over what survived. The book whose ticket has the
  // best combined EV wins — which is not the same as the book that is longest on
  // any one leg, because a book that is best on two legs and not pricing the
  // third cannot print the ticket at all.
  //
  // THE EXCHANGE IS NEVER A CANDIDATE. It does not write multis, its price is an
  // offer rather than a quote, and it charges commission on the win — the same
  // exclusion `bestPriceToTake` makes for a single, for the same reasons.
  const bookChoice = { bookmaker: null, candidates: [], refused: [], stats: null };
  if (bookmakerRule === 'best-book') {
    const books = new Set();
    for (const match of universe.matches.values()) {
      if (filters.sports.length && !filters.sports.includes(match.sport)) continue;
      for (const selection of match.selections ?? ['home', 'away']) {
        for (const row of match.prices?.[selection] ?? []) {
          if (!NOT_A_BET_BOOK.includes(row.bookmaker)) books.add(row.bookmaker);
        }
      }
    }
    // Sorted, so two boards carrying the same books in a different order choose
    // the same one; ties go to the profile's book, which is the account the
    // punter already holds.
    let bestEv = -Infinity;
    for (const key of [...books].sort()) {
      const run = collect(atBook(key));
      const picked = chooseLegs(legRule, poolOf(rankedOf(run.candidates)), combiner)
        .slice(0, combiner.maxLegs);
      const fills = picked.length >= combiner.minLegs;
      const ev = fills ? ticketEv(picked) : -Infinity;
      const tie = Math.abs(ev - bestEv) <= TIE_EPS;
      const wins = bookChoice.bookmaker === null
        ? true
        : (ev > bestEv + TIE_EPS || (tie && key === ctx.bookmaker));
      // A book that cannot fill a ticket still stands in for the blank note —
      // "looked at 6 legs, the best edge was +1.2%" is about the board, and a
      // run with no candidates at all could not say even that.
      if (wins && (fills || bestEv === -Infinity)) {
        bookChoice.bookmaker = key;
        bookChoice.candidates = run.candidates;
        // THE REFUSALS OF THE BOOK THAT WON, not of all of them. Every book on
        // the board gets the whole run done at its own prices, so keeping the
        // lot would record the same leg turned down a dozen times over — once
        // per shop the ticket was never going to be written at.
        bookChoice.refused = run.refused;
        bookChoice.stats = run.stats;
        bestEv = ev;
      }
    }
  }

  const { candidates, refused, stats } = bookChoice.stats
    ? { candidates: bookChoice.candidates, refused: bookChoice.refused, stats: bookChoice.stats }
    : collect(bookmakerRule === 'best-book' ? atBook(ctx.bookmaker) : priceLeg);

  // Once for the run, never per leg: a punter needs to know the market they
  // asked for was not the one they got, not to be told six times.
  if (stats.marketAbsent || stats.marketSelf) {
    const why = [];
    if (stats.marketAbsent) why.push(`absent on ${stats.marketAbsent}`);
    if (stats.marketSelf) why.push(`the book being bet at on ${stats.marketSelf}`);
    notes.push(`market book ${marketBook} ${why.join(', ')} of ${stats.marketLegs} legs, consensus used`);
  }

  // THE MODEL HAS NOT EARNED A PRICE YET (engine review #12), said once and
  // only where nothing else survived: a run that printed legs off a proven
  // sport and skipped an unproven one has printed what it could, and the note
  // would be about the half of the board that is missing.
  const unprovenAll = stats.unproven > 0 && candidates.length === 0;
  if (unprovenAll) notes.push(MODEL_UNPROVEN);

  // A blend needs a market to correct, and a board of one book has none.
  if (stats.noMarket) {
    notes.push(`${stats.noMarket} leg${stats.noMarket === 1 ? '' : 's'} with no market price to correct, skipped`);
  }

  // The races this deal was too late for, said once. A card whose next six are
  // all in the gates is a fact about the afternoon; without the line the
  // strategy simply looks like it found nothing.
  if (stats.tooSoon) {
    notes.push(`${stats.tooSoon} race${stats.tooSoon === 1 ? '' : 's'} jumping inside ${RACING_LEAD_MS / 60_000} minutes skipped`);
  }

  // Said once, for the same reason: a day where every leg was dropped for a
  // thin market is not a day with nothing on the board, and the two look
  // identical from the outside.
  if (stats.thinMarket) {
    notes.push(`${stats.thinMarket} leg${stats.thinMarket === 1 ? '' : 's'} priced by fewer than ${minBooks} books, skipped`);
  }

  const ranked = rankedOf(candidates);

  // Said once and only where it happened: the shop the ticket names is not
  // where every leg on it is. Counted off the ACTIVE legs, because a race that
  // was never going to print is not a hole in the punter's own book's card.
  const fellBack = new Set(ranked.filter((c) => c.viaBestPrice).map((c) => c.matchKey)).size;
  if (fellBack && ctx.bookmakerName) {
    notes.push(`${ctx.bookmakerName} is not pricing ${fellBack} of these races · best price used`);
  }
  // Only where a screen actually ran. With no model nothing was screened, so
  // an empty list means the board itself had nothing in the filters — which is
  // what `candidates` says, and what the pad prints instead.
  if (ranked.length === 0 && screens && !unprovenAll) notes.push('no leg cleared the edge and EV thresholds');

  // --- tickets ---------------------------------------------------------------
  const baseId = `${forDate}-${strategy.id}-s${seed}`;
  const taken = new Set();
  const tickets = [];
  // THE DAY'S EXPOSURE BUDGET, AND IT IS THE DAY'S (engine review #13).
  //
  // This was a local `stakedSoFar`, which capped one RUN: each ticket no longer
  // saw the whole allowance, but every strategy on the pad still did, so six of
  // them at 10% of bankroll each could put 60% of it on the counter with
  // nothing in the app adding it up. The budget now arrives on `ctx` — created
  // once per deal in store.js and handed to every run in the pass — and a run
  // with no budget on its context makes its own, so a strategy run alone (a
  // backtest, a test, the picks screen) behaves exactly as it did.
  const budget = ctx.budget ?? createBudget();
  let saidShortOfLongShots = false;
  let saidNoBankroll = false;
  let saidNoEdgeToStake = false;
  // A singles strategy's ticket count is its own cap, not the combiner's: each
  // ticket is one leg, so "how many tickets" and "how many legs to print" are
  // the same question and `legRule.max` is where it is answered.
  const ticketCount = singles ? legRule.max : combiner.tickets;
  for (let i = 0; i < ticketCount; i++) {
    const pool = poolOf(ranked.filter((c) => !taken.has(c.matchKey)));
    const picked = chooseLegs(legRule, pool, combiner).slice(0, combiner.maxLegs);
    if (picked.length < combiner.minLegs) {
      // A singles run asks for up to `max` and prints what it found. Three
      // mispriced legs on the board is the good day, not the baseline, so
      // "ticket 2: 0 legs available" would be a complaint about the market.
      if (ranked.length && !singles) {
        notes.push(`ticket ${i + 1}: ${picked.length} legs available, ${combiner.minLegs} needed`);
      }
      break;
    }
    for (const c of picked) taken.add(c.matchKey);

    // A mix that could not find its long shots printed a different ticket from
    // the one it was asked for, and has to say so. Counted off the printed
    // legs rather than the search, because that is the number a punter can
    // check against the ticket in front of them. Once per run, not per ticket.
    if (legRule.type === 'mix' && !saidShortOfLongShots) {
      const found = picked.filter((c) => inBand(c.price, legRule.longBand)).length;
      if (found < legRule.longShots) {
        const shots = `long shot${legRule.longShots === 1 ? '' : 's'}`;
        notes.push(`only ${found} of ${legRule.longShots} ${shots} found today`);
        saidShortOfLongShots = true;
      }
    }

    // Printed ticket order: earliest start first, as generateTickets does.
    const ordered = picked
      .slice()
      .sort((a, b) => a.kickoff.localeCompare(b.kickoff) || a.key.localeCompare(b.key));
    // THE TICKET IS AT ONE BOOK AND EVERY LEG ON IT CLEARED THAT BOOK'S SCREEN
    // (engine review #11). The Whelan ladder takes its leg count off these legs'
    // probabilities, and those probabilities are only about a bet that can be
    // placed if the prices under them all sit at the one counter. Asserted here
    // rather than trusted: the candidates were built at this book, so a failure
    // would be a bug in the search rather than a fact about the board.
    if (bookChoice.bookmaker) assertOneBook(ordered, bookChoice.bookmaker);
    // THE ESTIMATION-ERROR HAIRCUT (engine review #10). Every leg's claim is
    // shrunk toward its own fair price before the product is taken, by more the
    // more legs there are — see `SHRINK_BY_LEGS` in combine.js for why the error
    // in a multi compounds rather than averaging out. `pUsed` is what the
    // printed chance, EV and stake are all read off; each leg keeps its own
    // unshrunk `modelProbability`, which is what the per-leg lines print.
    const shrink = shrinkFactor(ordered.length);
    const used = ordered.map((c) => ({ ...c, p: shrinkToward(c.pModel, c.pFair, ordered.length) }));
    const combined = combineLegs(used);
    const pFairCombined = ordered.reduce((acc, c) => acc * c.pFair, 1);

    let stake = ctx.stake;
    let stakeRule = 'flat';
    if (staking.type === 'fractional-kelly') {
      if (!Number.isFinite(ctx.bankroll)) {
        // FLAT, AND SAID ONCE. Every model strategy asks for Kelly now, and a
        // punter with no bankroll in Settings would otherwise read the same
        // sentence three times under one section.
        if (!saidNoBankroll) {
          notes.push('fractional-kelly needs a bankroll; staked flat instead');
          saidNoBankroll = true;
        }
      } else {
        // validateStrategy has already checked exposurePct is in (0, 1] for
        // every fractional-kelly strategy, so this is never NaN here.
        const exposureLeft = budgetLeft(budget, ctx.bankroll, staking.exposurePct);
        const asked = {
          p: combined.p,
          price: combined.price,
          bankroll: ctx.bankroll,
          fraction: staking.fraction,
          capPct: staking.capPct,
          // WHAT THE BOOK WILL ACTUALLY TAKE. A stake Kelly asks for that the
          // punter's own shop refuses is not a stake, it is a ticket that
          // cannot be placed printed as though it could — and on racing the cap
          // is a real number (the minimum-bet schedules) rather than a guess.
          // Null on a multi shopped across the board, which names no shop.
          maxStake: bookCapFor(ctx, ordered) ?? Infinity,
          // Half the fraction on a multi, whatever the leg count
          // (`KELLY_MULTI_CAP`): the probability Kelly is being handed is a
          // product of estimates that share their errors, and the haircut above
          // takes that out of the number rather than out of the stake.
          legs: ordered.length,
        };
        // What Kelly and the cap asked for, and then what the day can afford.
        // Both, rather than one call with `exposureLeft` in it, because the
        // difference between them is the whole of what the budget did: a ticket
        // trimmed to the remainder is a ticket, and one trimmed to under a
        // dollar is the day being over.
        const want = stakeFor(asked);
        stake = stakeFor({ ...asked, exposureLeft });
        stakeRule = 'fractional-kelly';

        // NOTHING TO STAKE BY. Kelly's fraction is zero on a ticket with no
        // edge at the chance it is claiming — a shape strategy staked this way,
        // or a multi the haircut has taken under water — and a ticket printed
        // at $0 is not a bet, it is a bug wearing a price. It stakes the flat
        // default and says which rule it used, so the ticket and its own stake
        // line cannot disagree.
        if (want === 0) {
          stake = ctx.stake;
          stakeRule = 'flat';
          if (!saidNoEdgeToStake) {
            notes.push('no edge to stake by; staked flat instead');
            saidNoEdgeToStake = true;
          }
        } else if (stake < want && stake < MIN_STAKE) {
          // WHAT THE DAY HAS LEFT IS THE STAKE, until there is not enough left
          // to be a bet: a ticket that would have gone over prints at the
          // remainder; under a dollar there is nothing to print, the pad says
          // the day is spent, and the run stops — every ticket after this one
          // would be the same few cents.
          notes.push(BUDGET_SPENT);
          // The legs that were about to print. They cleared every screen the
          // app has and were turned down by the day's own exposure, which is
          // the one refusal that is about the punter rather than the price.
          for (const c of ordered) refused.push(refusalOf(c, 'budget'));
          break;
        } else {
          // Only a ticket actually staked BY the budget spends it. A flat
          // fallback above is the same money the pad has always put on and is
          // not exposure anybody chose, the same way Classic's stake is not.
          budget.staked = cents(budget.staked + stake);
        }
      }
    }

    const id = i === 0 ? baseId : `${baseId}-${i + 1}`;
    const eventIdOf = (c) => c.providerEventId ?? c.matchKey;
    tickets.push({
      multi: {
        id,
        // THE HONEST READING OF THE CHANCE, which is the DEVIGGED one.
        //
        // The tier used to be `tierFor(combined.p)`, the product of whatever
        // the strategy's own probability source said. For Classic that is the
        // implied product with the vig still in it, so a ticket was coloured by
        // a number a bookmaker's margin had already flattered; for a model
        // strategy it is the model's own claim, which is the thing engine
        // review #12 says the app should trust least. `fairProbability` is the
        // product of each leg's price with its own book's margin stripped —
        // the reading the punter can check — and every printer in the app now
        // labels off it. See lib/tier.js and tier-from-chance.test.js.
        tier: tierFor(Number.isFinite(pFairCombined) ? pFairCombined : combined.p),
        forDate,
        status: 'proposed',
        stake,
        // WHICH COUNTER THIS TICKET IS PLACED AT, and now every ticket this
        // engine prints can answer.
        //
        // "Best price" was an instruction to go and shop, and it was the only
        // honest thing a multi whose legs sat at three different books could
        // say — but a multi like that was never placeable at all (engine review
        // #4), so it is not printed any more. What is left names a book: a
        // single names its own, a `best-book` multi names the one its legs were
        // all re-priced at, and everything else names the account in Settings.
        // The key rides beside the name so the screens and the bet record do
        // not have to match a display string back to a bookmaker.
        bookmaker: singles || bookmakerRule === 'best-book'
          ? ordered[0].bookmakerName
          : ctx.bookmakerName,
        ...(singles || bookmakerRule === 'best-book'
          ? { bookmakerKey: ordered[0].bookmaker }
          : {}),
        // When the PRICES were read, which on a set dealt hours after the board
        // was fetched is not `ctx.asOf` — that is the deal's own cutoff.
        oddsFetchedAt: new Date(ctx.oddsFetchedAt ?? ctx.asOf ?? universe.asOf).toISOString(),
        combinedOdds: combined.price,
        combinedProbability: combined.p,
        potentialReturn: cents(stake * combined.price),
        strategyId: strategy.id,
        strategyVersion: strategy.version,
        label: strategy.name,
        // THE MODEL'S CLAIM AS THIS TICKET IS ALLOWED TO MAKE IT — shrunk
        // toward the fair price by the leg count (engine review #10), which is
        // why `edge` here can be a fraction of the per-leg edges printed
        // underneath it. `shrink` is how much of the claim survived, carried so
        // the figure can be explained rather than just being smaller.
        modelProbability: combined.p,
        fairProbability: pFairCombined,
        edge: combined.p - pFairCombined,
        ev: combined.p * combined.price - 1,
        ...(shrink < 1 ? { shrink } : {}),
        stakeRule,
        // THE TWO NUMBERS THE PLACE ALERT SAYS OUT LOUD — "¼ Kelly of $500 ·
        // capped at 2%". Carried on the ticket rather than looked up from the
        // strategy when it is placed: a fork can stake differently from the
        // built-in it came from, and a record written weeks ago should print
        // the rule it was actually staked by.
        ...(stakeRule === 'fractional-kelly'
          ? { stakeFraction: staking.fraction, stakeCapPct: staking.capPct }
          : {}),
        // HOW THE BOOK ON THIS TICKET WAS ARRIVED AT, beside how the stake was.
        // A one-leg ticket from a singles strategy is one leg on purpose; a
        // one-leg ticket from `best-book` is Whelan's answer to a multi, and
        // the two say different things on the paper (`askedForSingle` in
        // Ticket.vue) even though both name a book.
        bookmakerRule,
      },
      legs: ordered.map((c) => ({
        id: `${id}-${eventIdOf(c)}`,
        multiId: id,
        eventId: eventIdOf(c),
        // THE PROVIDER'S OWN ID, kept apart from `eventId` (which falls back to
        // the matchKey so a leg always has something to be named after). This
        // one is null or it is the provider's, and settlement joins a leg to a
        // result row on it before it looks at the key — the key is re-derived at
        // both ends, this is not. See `rowsFor` in stores/settlement.js.
        providerEventId: c.providerEventId ?? null,
        selection: legSelection(c),
        match: c.match,
        sportTitle: c.sportTitle ?? c.competition,
        commenceTime: c.kickoff,
        price: c.price,
        impliedProbability: 1 / c.price,
        matchKey: c.matchKey,
        sport: c.sport,
        bookmakerKey: c.bookmaker,
        // THE WAY TO THE BOOK THIS LEG IS AT, carried from the candidate and
        // frozen onto the leg: the ticket outlives the board it was printed
        // from, and the button under it opens this. Absent where the book
        // published nothing, which is what every leg carried until now.
        ...(c.link ? { link: c.link, linkKind: c.linkKind } : {}),
        modelProbability: c.pModel,
        fairProbability: c.pFair,
        // WHAT THE MODEL CONTRIBUTED TO THAT FIGURE (engine review #12): `b2`,
        // the fitted weight on the model's own claim beside the market's price,
        // and the raw claim it was applied to. The explain sheet reads them;
        // omitted entirely where the probability is a market price, so a
        // market strategy's leg and every stored leg read as they always did.
        ...(Number.isFinite(c.blendWeight)
          ? { blendWeight: c.blendWeight, rawProbability: c.pRaw }
          : {}),
        // WHERE THE FAIR PRICE ON THIS LEG WAS READ (F1b), and the book it was
        // read off. The ticket's Fair line says it in words ("fair from the
        // exchange" / "fair from local books only", lib/fair-copy.js). Omitted
        // where the probability is the leg's own book devigged, so a Classic
        // leg and every stored leg read exactly as they always did.
        ...(c.fairMode ? { fairMode: c.fairMode, fairSource: c.fairSource ?? null } : {}),
        // WHAT THE THREE DEVIGS MAKE OF THE SAME PRICE. The ticket screen
        // prints "fair 2.90 to 3.40" instead of one number where the band is
        // wide (`FAIR_SPREAD_SHOWN`), because a single figure there would be
        // the app choosing a method and not saying so. Omitted entirely on a
        // market with no band, so a hand-logged leg and an old stored one read
        // exactly as they always did.
        ...(c.pUncertainty !== null && c.pUncertainty !== undefined ? {
          fairProbabilityLow: c.pFairLow,
          fairProbabilityHigh: c.pFairHigh,
          fairSpread: c.pUncertainty,
        } : {}),
        edge: c.edge,
        // The machine-readable half of `selection`. Settlement compares this
        // to result.winner; the printed text is for the punter, not the code.
        side: c.selection,
        // THE FEED'S OWN WORD FOR "IT HAS JUMPED", carried onto the leg because
        // the leg is all `ticketState` (lib/timeline.js) gets to read. A race
        // that went early or late is started when the card says it is, not when
        // the clock says it should have been. Absent on a fixture, so nothing
        // about a football leg changes.
        ...(c.inplayAt ? { inplayAt: c.inplayAt } : {}),
        ...(c.marketClosedAt ? { marketClosedAt: c.marketClosedAt } : {}),
      })),
    });
  }

  return { tickets, candidates, refused, notes };
}
